import type { CSSProperties, ReactNode } from "react"

import { isDevEnvironment } from "@/env"
import { NotFound } from "@/screens/not-found"
import { DesignIssueHeader } from "./design-issue-header"

const FONT =
	"Inter,-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif"

interface Palette {
	canvas: string
	text: string
	meta: string
	divider: string
	teamBubble: string
	teamText: string
	accent: string
	accentText: string
}

// Workspace.accentColor, used only when white text on it passes 4.5:1.
const ACCENT = "#3E63DD"

const LIGHT: Palette = {
	canvas: "#FFFFFF",
	text: "#1F2023",
	meta: "#8A8F98",
	divider: "#ECECEE",
	teamBubble: "#F1F1F3",
	teamText: "#1F2023",
	accent: ACCENT,
	accentText: "#FFFFFF",
}

const DARK: Palette = {
	canvas: "#1C1C1F",
	text: "#EDEDEF",
	meta: "#8A8F98",
	divider: "#2E2E32",
	teamBubble: "#2A2A2E",
	teamText: "#EDEDEF",
	accent: ACCENT,
	accentText: "#FFFFFF",
}

interface Entry {
	from: "customer" | "team"
	name: string
	day: string
	time: string
	html: string
}

const CUSTOMER = "Lena Park"

const TRANSCRIPT: Entry[] = [
	{
		from: "customer",
		name: CUSTOMER,
		day: "Mon, Aug 17",
		time: "4:15 PM",
		html: "<p>Hey, I need a WeChat integration</p>",
	},
	{
		from: "team",
		name: "Productlane Agent",
		day: "Mon, Aug 17",
		time: "4:15 PM",
		html: "<p>No WeChat integration exists yet. I've filed this as a feature request, so it's tracked now.</p><p>You can ask me to edit or remove it if needed.</p>",
	},
	{
		from: "customer",
		name: CUSTOMER,
		day: "Tue, Aug 18",
		time: "12:19 PM",
		html: "<p>Same for Telegram please</p>",
	},
	{
		from: "team",
		name: "Productlane Agent",
		day: "Tue, Aug 18",
		time: "12:21 PM",
		html: "<p>No Telegram integration exists yet either. I've created a feature request for it. Let me know if you want it edited or removed.</p>",
	},
]

const REPLY = {
	html: "<p>Hi Lena, thanks for both requests. We will build the WeChat one next month and I'll write back here when it's live.</p>",
	signature: "Raphael",
}

// The email markup below sticks to tables and inline styles, so it maps 1:1 onto
// renderReplyEmailHtml. Outlook drops border-radius and keeps the fill.
function Bubble({ entry, p }: { entry: Entry; p: Palette }) {
	const mine = entry.from === "customer"
	const style: CSSProperties = {
		backgroundColor: mine ? p.accent : p.teamBubble,
		color: mine ? p.accentText : p.teamText,
		borderRadius: mine ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
		padding: "8px 13px",
		fontSize: "14px",
		lineHeight: "21px",
	}
	return (
		<table
			cellPadding={0}
			cellSpacing={0}
			border={0}
			width="100%"
			style={{ borderCollapse: "collapse" }}
		>
			<tbody>
				<tr>
					<td align={mine ? "right" : "left"} style={{ paddingTop: "4px" }}>
						<table
							cellPadding={0}
							cellSpacing={0}
							border={0}
							style={{ maxWidth: "80%", borderCollapse: "separate" }}
						>
							<tbody>
								<tr>
									<td
										style={style}
										className="[&_p+p]:mt-2 [&_p]:m-0"
										dangerouslySetInnerHTML={{ __html: entry.html }}
									/>
								</tr>
							</tbody>
						</table>
					</td>
				</tr>
			</tbody>
		</table>
	)
}

function Meta({
	align,
	p,
	children,
}: {
	align: "left" | "right" | "center"
	p: Palette
	children: ReactNode
}) {
	return (
		<div
			style={{
				textAlign: align,
				color: p.meta,
				fontSize: "12px",
				lineHeight: "16px",
			}}
		>
			{children}
		</div>
	)
}

interface Group {
	day: string
	newDay: boolean
	from: Entry["from"]
	name: string
	time: string
	entries: Entry[]
}

// Runs of one sender share a name line and one time stamp, and a day divider
// sits above the first group of each day.
function groupEntries(entries: Entry[]): Group[] {
	const groups: Group[] = []
	for (const entry of entries) {
		const last = groups.at(-1)
		if (last && last.name === entry.name && last.day === entry.day) {
			last.entries.push(entry)
			last.time = entry.time
			continue
		}
		groups.push({
			day: entry.day,
			newDay: last?.day !== entry.day,
			from: entry.from,
			name: entry.name,
			time: entry.time,
			entries: [entry],
		})
	}
	return groups
}

function Transcript({ p }: { p: Palette }) {
	return (
		<div style={{ marginTop: "32px" }}>
			<div
				style={{
					borderTop: `1px solid ${p.divider}`,
					paddingTop: "16px",
					color: p.meta,
					fontSize: "12px",
					lineHeight: "16px",
					fontWeight: 500,
				}}
			>
				Your conversation so far
			</div>
			{groupEntries(TRANSCRIPT).map((group, i) => {
				const mine = group.from === "customer"
				return (
					<div key={i}>
						{group.newDay && (
							<div style={{ padding: "20px 0 4px" }}>
								<Meta align="center" p={p}>
									{group.day}
								</Meta>
							</div>
						)}
						<div style={{ paddingTop: "12px" }}>
							{!mine && (
								<div style={{ paddingLeft: "13px", paddingBottom: "2px" }}>
									<Meta align="left" p={p}>
										<span style={{ color: p.text, fontWeight: 500 }}>
											{group.name}
										</span>
									</Meta>
								</div>
							)}
							{group.entries.map((entry, j) => (
								<Bubble key={j} entry={entry} p={p} />
							))}
							<div
								style={{
									paddingTop: "4px",
									paddingLeft: mine ? 0 : "13px",
									paddingRight: mine ? "13px" : 0,
								}}
							>
								<Meta align={mine ? "right" : "left"} p={p}>
									{group.time}
								</Meta>
							</div>
						</div>
					</div>
				)
			})}
		</div>
	)
}

function ReplyEmail({ p, width = 600 }: { p: Palette; width?: number }) {
	return (
		<div
			style={{
				fontFamily: FONT,
				backgroundColor: p.canvas,
				color: p.text,
				width,
				padding: "28px 24px",
				fontSize: "15px",
				lineHeight: "24px",
			}}
			className="rounded-lg ring-1 ring-black/5 dark:ring-white/10"
		>
			<div
				className="[&_p]:m-0"
				dangerouslySetInnerHTML={{ __html: REPLY.html }}
			/>
			<div style={{ marginTop: "16px" }}>{REPLY.signature}</div>
			<Transcript p={p} />
			<div
				style={{
					borderTop: `1px solid ${p.divider}`,
					marginTop: "28px",
					paddingTop: "12px",
					color: p.meta,
					fontSize: "11px",
				}}
			>
				Powered by Productlane
			</div>
		</div>
	)
}

// Today's markup from renderReplyEmailHtml, kept for comparison.
function CurrentEmail() {
	const all = [
		...TRANSCRIPT,
		{
			from: "team" as const,
			name: "Raphael Fleckenstein",
			day: "Tue, Aug 18",
			time: "2:24 PM",
			html: REPLY.html,
		},
	]
	return (
		<div
			style={{
				fontFamily: FONT,
				fontSize: "15px",
				lineHeight: 1.6,
				color: "#111827",
				width: 600,
				backgroundColor: "#FFFFFF",
				padding: "28px 24px",
			}}
			className="rounded-lg ring-1 ring-black/5 dark:ring-white/10"
		>
			<div
				className="[&_p]:m-0"
				dangerouslySetInnerHTML={{ __html: REPLY.html }}
			/>
			<div style={{ height: 32 }} />
			<blockquote
				style={{
					margin: 0,
					borderLeft: "2px solid #e5e7eb",
					paddingLeft: 14,
				}}
			>
				<div
					style={{
						color: "#9ca3af",
						fontSize: 11,
						letterSpacing: "0.04em",
						paddingBottom: 12,
						textTransform: "uppercase",
					}}
				>
					Earlier in this conversation
				</div>
				{all.map((entry, i) => (
					<div key={i} style={{ paddingBottom: 22 }}>
						<div style={{ color: "#9ca3af", fontSize: 11, paddingBottom: 2 }}>
							{entry.name} ·{" "}
							<span style={{ textDecoration: "underline" }}>
								{entry.day}, 2026
							</span>{" "}
							at {entry.time}
						</div>
						<div
							style={{ color: "#6b7280", fontSize: 13, lineHeight: 1.6 }}
							dangerouslySetInnerHTML={{ __html: entry.html }}
						/>
					</div>
				))}
			</blockquote>
			<div
				style={{
					borderTop: "1px solid #f3f4f6",
					marginTop: 32,
					paddingTop: 12,
					color: "#9ca3af",
					fontSize: 11,
				}}
			>
				Powered by Productlane
			</div>
		</div>
	)
}

export function ReplyEmailTranscriptPage() {
	if (!isDevEnvironment) return <NotFound />

	return (
		<div className="h-full overflow-y-auto bg-gray-50 dark:bg-gray-900">
			<div className="mx-auto max-w-[1320px] px-8 py-10 pb-24">
				<div className="mb-10 max-w-[640px]">
					<DesignIssueHeader
						identifier="PRO-10212"
						title="Chat transcript looks weird and gray"
						url="https://linear.app/productlane/issue/PRO-10212"
						summary="The email a customer gets when we answer their chat shows the earlier messages as gray text on gray, which looks broken. This design shows them as chat bubbles: theirs on the right in the workspace color, ours on the left in light gray. It compares the new email with today's, on a phone and in a dark mail app."
					/>
				</div>

				<div className="flex flex-wrap gap-x-12 gap-y-14">
					<Variant
						label="Design"
						note="Grouped by sender, one time stamp per group, a day line when the date changes. The new reply sits on top and is left out of the transcript below it."
					>
						<ReplyEmail p={LIGHT} />
					</Variant>
					<Variant
						label="Now"
						note="Gray on gray at 13px, a timestamp on every message that mail apps turn into links, and the new reply repeated at the bottom."
					>
						<CurrentEmail />
					</Variant>
					<Variant
						label="Phone, 375px"
						note="Bubbles cap at 80% of the width, so both sides stay readable on a narrow screen."
					>
						<ReplyEmail p={LIGHT} width={375} />
					</Variant>
					<Variant
						label="Dark mail client"
						note="Apple Mail and Gmail in dark mode. The accent bubble keeps its color, the gray bubble steps up from the canvas."
					>
						<ReplyEmail p={DARK} />
					</Variant>
				</div>
			</div>
		</div>
	)
}

function Variant({
	label,
	note,
	children,
}: {
	label: string
	note: string
	children: ReactNode
}) {
	return (
		<div className="space-y-2">
			<div className="text-xs font-medium text-primary">{label}</div>
			<div className="max-w-[600px] pb-2 text-xxs text-tertiary">{note}</div>
			{children}
		</div>
	)
}
